// AI Training Service for Way2Will
// Handles storing and retrieving user preferences for AI personalization

// Default preferences
export interface AIPreferences {
    // Auto-scheduling aggressiveness (1-10)
    schedulingAggressiveness: number;

    // Buffer time between meetings (in minutes)
    bufferTimeBetweenMeetings: number;

    // Work hours
    workStartTime: string; // HH:MM format
    workEndTime: string;

    // Focus session defaults
    defaultFocusDuration: number; // minutes
    preferredBreakDuration: number; // minutes

    // Task defaults
    defaultTaskDuration: number; // minutes
    autoAssignDeadlines: boolean;

    // Communication style
    briefingVerbosity: 'concise' | 'detailed' | 'comprehensive';

    // Priority defaults by project (projectId -> priority)
    projectPriorityDefaults: Record<string, 'low' | 'medium' | 'high'>;
}

// Default values
export const DEFAULT_AI_PREFERENCES: AIPreferences = {
    schedulingAggressiveness: 5,
    bufferTimeBetweenMeetings: 15,
    workStartTime: '09:00',
    workEndTime: '17:00',
    defaultFocusDuration: 25,
    preferredBreakDuration: 5,
    defaultTaskDuration: 30,
    autoAssignDeadlines: false,
    briefingVerbosity: 'detailed',
    projectPriorityDefaults: {},
};

// Storage key
const STORAGE_KEY = 'way2will_ai_preferences';

/**
 * Get stored AI preferences
 */
export function getAIPreferences(): AIPreferences {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_AI_PREFERENCES;

    try {
        return { ...DEFAULT_AI_PREFERENCES, ...JSON.parse(stored) };
    } catch {
        return DEFAULT_AI_PREFERENCES;
    }
}

/**
 * Save AI preferences
 */
export function saveAIPreferences(preferences: Partial<AIPreferences>): void {
    const current = getAIPreferences();
    const updated = { ...current, ...preferences };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
}

/**
 * Reset to default preferences
 */
export function resetAIPreferences(): void {
    localStorage.removeItem(STORAGE_KEY);
}

/**
 * Generate AI system prompt additions based on preferences
 */
export function generatePreferencePrompt(preferences: AIPreferences): string {
    const lines: string[] = [
        '## User Preferences',
        '',
        `- Scheduling aggressiveness: ${preferences.schedulingAggressiveness}/10 (${preferences.schedulingAggressiveness <= 3 ? 'conservative' : preferences.schedulingAggressiveness >= 7 ? 'aggressive' : 'moderate'})`,
        `- Preferred buffer between meetings: ${preferences.bufferTimeBetweenMeetings} minutes`,
        `- Work hours: ${preferences.workStartTime} to ${preferences.workEndTime}`,
        `- Default focus session: ${preferences.defaultFocusDuration} minutes with ${preferences.preferredBreakDuration} minute breaks`,
        `- Default task duration estimate: ${preferences.defaultTaskDuration} minutes`,
        `- Auto-assign deadlines: ${preferences.autoAssignDeadlines ? 'Yes' : 'No'}`,
        `- Briefing style preference: ${preferences.briefingVerbosity}`,
    ];

    if (Object.keys(preferences.projectPriorityDefaults).length > 0) {
        lines.push('', '### Project Priority Defaults:');
        for (const [projectId, priority] of Object.entries(preferences.projectPriorityDefaults)) {
            lines.push(`- Project ${projectId}: ${priority} priority`);
        }
    }

    return lines.join('\n');
}

/**
 * Get preference descriptions for UI
 */
export function getPreferenceDescriptions(): Record<keyof AIPreferences, string> {
    return {
        schedulingAggressiveness: 'How aggressively the AI should auto-schedule tasks. Higher = more packed schedule.',
        bufferTimeBetweenMeetings: 'Minimum time between meetings for preparation and travel.',
        workStartTime: 'When your work day typically begins.',
        workEndTime: 'When your work day typically ends.',
        defaultFocusDuration: 'Default length for focus sessions.',
        preferredBreakDuration: 'Preferred break length between focus sessions.',
        defaultTaskDuration: 'Default time estimate for new tasks without a specified duration.',
        autoAssignDeadlines: 'Automatically suggest deadlines for tasks without one.',
        briefingVerbosity: 'How detailed your morning briefings should be.',
        projectPriorityDefaults: 'Default priority level for tasks in specific projects.',
    };
}

// ============ USER IDENTITY ============

/**
 * User identity for personalized AI context
 * Frames all AI advice through the user's roles and goals
 * NOTE: Supports multiple concurrent roles (e.g., Developer, Father, Investor)
 */
export interface UserIdentity {
    roles: string[];            // Multiple concurrent roles, e.g., ['Developer', 'Father', 'Investor']
    primaryGoal: string;        // North Star, e.g., '$550k ARV by Q2'
    vipEntities: string[];      // Protected names that NEVER get auto-rescheduled
    vocabularyContext?: string; // Industry-specific terms
    productivityArchetype?: 'chaos-thriver' | 'structured-planner' | 'balanced-adapter' | 'zen-minimalist';
    // Legacy field for migration - will be converted to roles
    userRole?: string;
}

export const DEFAULT_USER_IDENTITY: UserIdentity = {
    roles: [],
    primaryGoal: '',
    vipEntities: [],
};

const IDENTITY_STORAGE_KEY = 'way2will_user_identity';

/**
 * Get stored user identity (with migration from legacy userRole)
 */
export function getUserIdentity(): UserIdentity {
    const stored = localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (!stored) return DEFAULT_USER_IDENTITY;

    try {
        const parsed = JSON.parse(stored);

        // Migration: Convert legacy userRole to roles array
        if (parsed.userRole && (!parsed.roles || parsed.roles.length === 0)) {
            parsed.roles = [parsed.userRole];
            delete parsed.userRole;
            // Save migrated version
            localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(parsed));
        }

        return { ...DEFAULT_USER_IDENTITY, ...parsed };
    } catch {
        return DEFAULT_USER_IDENTITY;
    }
}

/**
 * Save user identity (merges with existing)
 */
export function saveUserIdentity(identity: Partial<UserIdentity>): void {
    const current = getUserIdentity();
    const updated = { ...current, ...identity };

    // Remove legacy userRole field if present
    delete updated.userRole;

    localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(updated));
}

/**
 * Add roles to identity without duplicates
 */
export function addRolesToIdentity(newRoles: string[]): void {
    const current = getUserIdentity();
    const uniqueRoles = [...new Set([...current.roles, ...newRoles])];
    saveUserIdentity({ roles: uniqueRoles });
}

/**
 * Add VIP entities without duplicates
 */
export function addVipsToIdentity(newVips: string[]): void {
    const current = getUserIdentity();
    const uniqueVips = [...new Set([...current.vipEntities, ...newVips])];
    saveUserIdentity({ vipEntities: uniqueVips });
}

/**
 * Check if a title contains a VIP entity (case-insensitive)
 */
export function isVIPEntity(title: string, vipEntities: string[]): boolean {
    const titleLower = title.toLowerCase();
    return vipEntities.some(vip =>
        titleLower.includes(vip.toLowerCase())
    );
}

/**
 * Generate identity prompt for AI system instruction
 */
export function generateIdentityPrompt(identity: UserIdentity): string {
    if ((!identity.roles || identity.roles.length === 0) && !identity.primaryGoal) return '';

    const lines: string[] = [
        '',
        '## USER IDENTITY & CONTEXT',
    ];

    if (identity.roles && identity.roles.length > 0) {
        lines.push(`- Roles: ${identity.roles.join(', ')}`);
    }

    if (identity.primaryGoal) {
        lines.push(`- North Star Goal: ${identity.primaryGoal}`);
        lines.push('  (Reference this when prioritizing tasks and giving advice)');
    }

    if (identity.vipEntities && identity.vipEntities.length > 0) {
        lines.push(`- VIP Entities (NEVER auto-reschedule): ${identity.vipEntities.join(', ')}`);
    }

    if (identity.productivityArchetype) {
        const archetypeDescriptions: Record<string, string> = {
            'chaos-thriver': 'Thrives in dynamic environments, prefers flexibility',
            'structured-planner': 'Values order and detailed planning',
            'balanced-adapter': 'Mixes structure with adaptability',
            'zen-minimalist': 'Focuses on essentials, values simplicity',
        };
        lines.push(`- Productivity Style: ${archetypeDescriptions[identity.productivityArchetype]}`);
    }

    lines.push('');
    lines.push('Frame all advice through the lens of these roles and goals.');

    return lines.join('\n');
}

